import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

const TYPE_OPTIONS = [
  { value: "public",       label: "Public",       hint: "Anyone can join, listed in the gallery." },
  { value: "private",      label: "Private",      hint: "Only you + invited people, not listed." },
  { value: "invite_only",  label: "Invite Only",  hint: "Listed publicly, but the host must approve." },
  { value: "paid",         label: "Paid",         hint: "Charge waves for entry (50/50 split to you as pearls)." },
  { value: "subscription", label: "Subscription", hint: "Requires active VIP or Voice Pro subscription." },
];

const CATEGORY_OPTIONS = [
  "conference", "business", "workshop", "class",
  "coaching", "meeting", "church", "community",
  "creator", "team", "hangout",
];

export default function RoomsCreate() {
  const nav = useNavigate();
  const [config, setConfig] = useState({ enabled: true, max_participants: 50 });
  const [form, setForm] = useState({
    title: "", description: "",
    room_type: "public", category: "meeting",
    price_waves: 0, max_participants: 12,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/rooms/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Give your room a title."); return; }
    if (form.room_type === "paid" && !form.price_waves) {
      toast.error("Paid rooms need a wave price.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/rooms", {
        ...form,
        title: form.title.trim(),
        description: form.description.trim(),
        price_waves: Number(form.price_waves) || 0,
        max_participants: Number(form.max_participants) || 12,
      });
      toast.success("Room created — invite people to join.");
      nav(`/rooms/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create room");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[100dvh] bg-ink pb-32 px-5 pt-6" data-testid="rooms-create-page">
      <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream mb-4" data-testid="rooms-create-back">
        <ArrowLeft size={18} />
      </button>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-2xl bg-papaya/15 text-papaya flex items-center justify-center">
          <UsersThree size={22} weight="fill" />
        </div>
        <div>
          <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Start a Room</p>
          <h1 className="text-cream text-2xl font-black tracking-tighter">Meeting · Workshop · Conference</h1>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        <div>
          <label className="text-cream/70 text-[10px] tracking-[0.3em] uppercase font-bold block mb-1">Title</label>
          <input
            value={form.title}
            onChange={(e) => update("title", e.target.value)}
            placeholder="Soca dance masterclass"
            className="w-full bg-white/5 rounded-2xl px-4 py-3 text-cream border border-white/10 focus:border-papaya outline-none"
            data-testid="rooms-create-title"
          />
        </div>

        <div>
          <label className="text-cream/70 text-[10px] tracking-[0.3em] uppercase font-bold block mb-1">Description</label>
          <textarea
            rows={3}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            placeholder="Warm-ups, choreo breakdown, Q&A."
            className="w-full bg-white/5 rounded-2xl px-4 py-3 text-cream border border-white/10 focus:border-papaya outline-none resize-none"
            data-testid="rooms-create-description"
          />
        </div>

        <div>
          <label className="text-cream/70 text-[10px] tracking-[0.3em] uppercase font-bold block mb-2">Type</label>
          <div className="grid grid-cols-2 gap-2">
            {TYPE_OPTIONS.map((t) => (
              <button
                key={t.value}
                onClick={() => update("room_type", t.value)}
                className={`text-left rounded-2xl p-3 border transition-colors ${
                  form.room_type === t.value
                    ? "border-papaya bg-papaya/10"
                    : "border-white/10 bg-white/5 hover:border-papaya/40"
                }`}
                data-testid={`rooms-create-type-${t.value}`}
              >
                <p className="text-cream font-black text-sm">{t.label}</p>
                <p className="text-cream/50 text-[11px] mt-0.5">{t.hint}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-cream/70 text-[10px] tracking-[0.3em] uppercase font-bold block mb-1">Category</label>
            <select
              value={form.category}
              onChange={(e) => update("category", e.target.value)}
              className="w-full bg-white/5 rounded-2xl px-4 py-3 text-cream border border-white/10 outline-none"
              data-testid="rooms-create-category"
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c} className="bg-ink capitalize">{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-cream/70 text-[10px] tracking-[0.3em] uppercase font-bold block mb-1">Max participants</label>
            <input
              type="number" min={2} max={config.max_participants || 50}
              value={form.max_participants}
              onChange={(e) => update("max_participants", e.target.value)}
              className="w-full bg-white/5 rounded-2xl px-4 py-3 text-cream border border-white/10 outline-none"
              data-testid="rooms-create-max"
            />
          </div>
        </div>

        {form.room_type === "paid" && (
          <div>
            <label className="text-cream/70 text-[10px] tracking-[0.3em] uppercase font-bold block mb-1">Entry price (waves)</label>
            <input
              type="number" min={1} max={10000}
              value={form.price_waves}
              onChange={(e) => update("price_waves", e.target.value)}
              className="w-full bg-white/5 rounded-2xl px-4 py-3 text-cream border border-white/10 outline-none"
              data-testid="rooms-create-price"
            />
            <p className="text-cream/40 text-[11px] mt-1">50% of every entry converts to pearls for you.</p>
          </div>
        )}

        <button
          onClick={submit}
          disabled={busy || !form.title.trim()}
          className="w-full mt-2 py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:brightness-110 transition disabled:opacity-40"
          data-testid="rooms-create-submit"
        >{busy ? "Creating…" : "Create Room"}</button>
      </div>
    </div>
  );
}
